import React, { useRef, useState } from "react";
import {
  Button,
  Form,
  Input,
  Tabs,
  TabsProps,
  notification,
  Popconfirm,
} from "antd";
import { useParams } from "react-router-dom";
import JoditEditor from "jodit-react";
import { PlusCircleFilled } from "@ant-design/icons";
import { addRichTextToCategory } from "../actions";

type Props = {
  fetchData: () => void;
};

interface FormValues {
  title: {
    en: string;
    ar: string;
    fr: string;
  };
  content: {
    en: string;
    ar: string;
    fr: string;
  };
}

const AddCategoryRichText: React.FC<Props> = ({ fetchData }) => {
  const [form] = Form.useForm<FormValues>();
  const [api, contextHolder] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const { id, categoryId } = useParams(); // article id comes from route

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsLoading(true);
      const success = await addRichTextToCategory(
        id!,
        categoryId!,
        values.title,
        values.content
      );
      if (success) {
        fetchData();
        api.success({ message: "Rich text article added successfully" });
        form.resetFields();
      }
    } catch (err: any) {
      api.error({
        message: "Failed to add article",
        description:
          err?.response?.errors?.[0]?.message ||
          err?.message ||
          "Unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const languages = ["en", "ar", "fr"] as const;

  const tabItems: TabsProps["items"] = languages.map((lang) => ({
    key: lang,
    label: lang.toUpperCase(),
    children: (
      <>
        <Form.Item
          name={["title", lang]}
          label={`Title (${lang})`}
          rules={[{ required: true, message: `Please enter title in ${lang}` }]}
        >
          <Input placeholder={`Enter title in ${lang.toUpperCase()}`} />
        </Form.Item>

        <Form.Item
          name={["content", lang]}
          label={`Content (${lang})`}
          rules={[
            { required: true, message: `Please enter content in ${lang}` },
          ]}
        >
          <JoditEditor
            ref={useRef(null)}
            value={form.getFieldValue(["content", lang])}
            onBlur={(newContent) => {
              form.setFieldValue(["content", lang], newContent);
            }}
            config={{
              readonly: false,
              direction: lang === "ar" ? "rtl" : "ltr",
              placeholder: `Write content in ${lang.toUpperCase()}...`,
              height: "50vh",
            }}
          />
        </Form.Item>
      </>
    ),
  }));

  return (
    <Form form={form} layout="vertical">
      {contextHolder}
      <Tabs defaultActiveKey="en" items={tabItems} />

      <Form.Item shouldUpdate>
        {() => (
          <Popconfirm
            title="Are you sure you want to add this rich text article?"
            onConfirm={handleSubmit}
            okText="Yes"
            cancelText="No"
          >
            <Button
              icon={<PlusCircleFilled />}
              loading={isLoading}
              type="primary"
              className="w-full"
            >
              {isLoading ? "Saving..." : "Add Article"}
            </Button>
          </Popconfirm>
        )}
      </Form.Item>
    </Form>
  );
};

export default AddCategoryRichText;
